import { z } from "zod";
import { FamilyId } from "../types/branded";
import {
  QaTuitionBalanceResetApplyResultSchema,
  QaTuitionBalanceResetPreviewInputSchema,
  QaTuitionBalanceResetPreviewSchema,
  QaTuitionBalanceResetRecipeIdSchema,
} from "./qa-tuition-balance-reset";

export const DemoRolePlayerPersonaSchema = z.enum([
  "parent",
  "staff",
  "administrator",
  "super-administrator",
]);

export const DemoRolePlayerEnvironmentSchema = z.enum([
  "local",
  "ystnj-dev",
  "intellio-dev",
]);

export const DemoRolePlayerSurfaceSchema = z.enum([
  "students-families",
  "staff-tuition",
  "parent-tuition",
  "staff-financial-aid",
  "parent-financial-aid",
  "staff-admissions",
  "parent-enrollment",
]);

export const DemoRolePlayerRecipeIdSchema = z.union([
  QaTuitionBalanceResetRecipeIdSchema,
  z.literal("restore_financial_aid_application"),
  z.literal("restore_enrollment_contract"),
]);

export const DemoRolePlayerTargetSchema = z
  .object({
    environment: DemoRolePlayerEnvironmentSchema,
    orgId: z.string().uuid(),
    schoolId: z.string().min(1),
    tenantSlug: z.string().min(1),
    userEmail: z.string().email().optional(),
    familyId: FamilyId.optional(),
    financialAidApplicantId: z.string().min(1).optional(),
    financialAidApplicationId: z.string().uuid().optional(),
    admissionsApplicantId: z.string().uuid().optional(),
    staffId: z.string().uuid().optional(),
  })
  .strict()
  .superRefine((target, context) => {
    if (
      !target.userEmail &&
      !target.familyId &&
      !target.financialAidApplicantId &&
      !target.admissionsApplicantId &&
      !target.staffId
    ) {
      context.addIssue({
        code: "custom",
        message:
          "A role-player target requires at least one non-secret user, family, or staff locator.",
      });
    }
  });

export const DemoRolePlayerBaselineAssertionSchema = z.discriminatedUnion(
  "kind",
  [
    z
      .object({
        kind: z.literal("family_open_balance"),
        expectedCents: z.number().int().nonnegative().max(50_000_000),
      })
      .strict(),
    z
      .object({
        kind: z.literal("shared_family_identity"),
        surfaces: z
          .array(
            z.enum(["students-families", "staff-tuition", "parent-tuition"]),
          )
          .min(2),
      })
      .strict(),
    z
      .object({
        kind: z.literal("payment_history_preserved"),
        expected: z.literal(true),
      })
      .strict(),
    z
      .object({
        kind: z.literal("financial_aid_status"),
        expected: z.enum(["invited", "draft", "submitted"]),
      })
      .strict(),
    z
      .object({
        kind: z.literal("shared_financial_aid_identity"),
        surfaces: z
          .array(z.enum(["staff-financial-aid", "parent-financial-aid"]))
          .length(2),
      })
      .strict(),
    z
      .object({
        kind: z.literal("enrollment_contract_state"),
        expected: z.enum(["unsigned", "signed"]),
      })
      .strict(),
    z
      .object({
        kind: z.literal("shared_enrollment_contract_identity"),
        surfaces: z
          .array(z.enum(["staff-admissions", "parent-enrollment"]))
          .length(2),
      })
      .strict(),
    z
      .object({
        kind: z.literal("enrollment_documents_preserved"),
        expected: z.literal(true),
      })
      .strict(),
  ],
);

const DemoRolePlayerBaselineScenarioBaseSchema = z.object({
  id: z
    .string()
    .min(1)
    .regex(/^[a-z0-9][a-z0-9-]*$/),
  label: z.string().min(1),
  environments: z.array(DemoRolePlayerEnvironmentSchema).min(1),
  academicYear: z.string().regex(/^\d{4}-\d{4}$/),
  assertions: z.array(DemoRolePlayerBaselineAssertionSchema).min(1),
});

export const DemoRolePlayerBaselineScenarioSchema = z.discriminatedUnion(
  "recipeId",
  [
    DemoRolePlayerBaselineScenarioBaseSchema.extend({
      recipeId: QaTuitionBalanceResetRecipeIdSchema,
      targetOpenBalanceCents: z.number().int().nonnegative().max(50_000_000),
    }).strict(),
    DemoRolePlayerBaselineScenarioBaseSchema.extend({
      recipeId: z.literal("restore_financial_aid_application"),
      targetStatus: z.enum(["invited", "draft", "submitted"]),
    }).strict(),
    DemoRolePlayerBaselineScenarioBaseSchema.extend({
      recipeId: z.literal("restore_enrollment_contract"),
      targetState: z.enum(["unsigned", "signed"]),
    }).strict(),
  ],
);

export const DemoRolePlayerSchema = z
  .object({
    key: z
      .string()
      .min(1)
      .regex(/^[a-z0-9][a-z0-9-]*$/),
    displayLabel: z.string().min(1),
    persona: DemoRolePlayerPersonaSchema,
    owner: z.string().min(1),
    allowedRecipeIds: z.array(DemoRolePlayerRecipeIdSchema).min(1),
    targets: z.array(DemoRolePlayerTargetSchema).min(1),
    baselineScenarios: z.array(DemoRolePlayerBaselineScenarioSchema).min(1),
  })
  .strict()
  .superRefine((rolePlayer, context) => {
    const targetEnvironments = new Set<string>();
    rolePlayer.targets.forEach((target, index) => {
      if (targetEnvironments.has(target.environment)) {
        context.addIssue({
          code: "custom",
          message: `Duplicate ${target.environment} target for ${rolePlayer.key}.`,
          path: ["targets", index, "environment"],
        });
      }
      targetEnvironments.add(target.environment);
    });

    const scenarioIds = new Set<string>();
    rolePlayer.baselineScenarios.forEach((scenario, index) => {
      if (scenarioIds.has(scenario.id)) {
        context.addIssue({
          code: "custom",
          message: `Duplicate baseline scenario ${scenario.id} for ${rolePlayer.key}.`,
          path: ["baselineScenarios", index, "id"],
        });
      }
      scenarioIds.add(scenario.id);

      if (!rolePlayer.allowedRecipeIds.includes(scenario.recipeId)) {
        context.addIssue({
          code: "custom",
          message: `Baseline scenario ${scenario.id} uses an unsupported recipe.`,
          path: ["baselineScenarios", index, "recipeId"],
        });
      }

      scenario.environments.forEach((environment) => {
        const target = rolePlayer.targets.find(
          (candidate) => candidate.environment === environment,
        );
        if (!target) {
          context.addIssue({
            code: "custom",
            message: `Baseline scenario ${scenario.id} has no ${environment} target.`,
            path: ["baselineScenarios", index, "environments"],
          });
        } else if (
          scenario.recipeId === "restore_tuition_open_balance" &&
          !target.familyId
        ) {
          context.addIssue({
            code: "custom",
            message: `Tuition baseline scenario ${scenario.id} requires a family locator.`,
            path: ["targets"],
          });
        } else if (
          scenario.recipeId === "restore_financial_aid_application" &&
          (!target.familyId ||
            !target.financialAidApplicantId ||
            !target.financialAidApplicationId)
        ) {
          context.addIssue({
            code: "custom",
            message: `Financial Aid baseline scenario ${scenario.id} requires family, applicant, and application locators.`,
            path: ["targets"],
          });
        } else if (
          scenario.recipeId === "restore_enrollment_contract" &&
          (!target.familyId || !target.admissionsApplicantId)
        ) {
          context.addIssue({
            code: "custom",
            message: `Enrollment baseline scenario ${scenario.id} requires family and admissions applicant locators.`,
            path: ["targets"],
          });
        }
      });
    });
  });

export const DemoRolePlayerRegistrySchema = z
  .object({
    schemaVersion: z.literal(1),
    owner: z.string().min(1),
    approvalPolicy: z
      .object({
        addingRolePlayerRequiresHumanApproval: z.literal(true),
        destructiveRecipeRequiresHumanApproval: z.literal(true),
        instructions: z.string().min(1),
      })
      .strict(),
    rolePlayers: z.array(DemoRolePlayerSchema).min(1),
  })
  .strict()
  .superRefine((registry, context) => {
    const rolePlayerKeys = new Set<string>();
    const targetLocators = new Map<string, string>();

    registry.rolePlayers.forEach((rolePlayer, rolePlayerIndex) => {
      if (rolePlayerKeys.has(rolePlayer.key)) {
        context.addIssue({
          code: "custom",
          message: `Duplicate role-player key ${rolePlayer.key}.`,
          path: ["rolePlayers", rolePlayerIndex, "key"],
        });
      }
      rolePlayerKeys.add(rolePlayer.key);

      rolePlayer.targets.forEach((target, targetIndex) => {
        const locatorParts = [
          target.familyId ? `family:${target.familyId}` : null,
          target.financialAidApplicantId
            ? `financial-aid-applicant:${target.financialAidApplicantId}`
            : null,
          target.admissionsApplicantId
            ? `admissions-applicant:${target.admissionsApplicantId}`
            : null,
          target.staffId ? `staff:${target.staffId}` : null,
          target.userEmail ? `user:${target.userEmail.toLowerCase()}` : null,
        ].filter((value): value is string => Boolean(value));

        locatorParts.forEach((locator) => {
          const targetKey = [
            target.environment,
            target.orgId,
            target.schoolId,
            target.tenantSlug,
            locator,
          ].join(":");
          const existingOwner = targetLocators.get(targetKey);
          if (existingOwner && existingOwner !== rolePlayer.key) {
            context.addIssue({
              code: "custom",
              message: `Target locator is already assigned to ${existingOwner}.`,
              path: ["rolePlayers", rolePlayerIndex, "targets", targetIndex],
            });
          } else {
            targetLocators.set(targetKey, rolePlayer.key);
          }
        });
      });
    });
  });

export const DemoRolePlayerResolutionSchema = z
  .object({
    rolePlayerKey: z.string().min(1),
    environment: DemoRolePlayerEnvironmentSchema,
    found: z.boolean(),
    orgId: z.string().uuid().optional(),
    schoolId: z.string().min(1).optional(),
    tenantSlug: z.string().min(1).optional(),
    userEmail: z.string().email().optional(),
    familyId: FamilyId.optional(),
    financialAidApplicantId: z.string().min(1).optional(),
    financialAidApplicationId: z.string().min(1).optional(),
    financialAidStatus: z.string().min(1).optional(),
    staffId: z.string().uuid().optional(),
    studentFamilyIds: z.array(FamilyId).optional(),
    surfaceFamilyIds: z
      .object({
        "students-families": FamilyId.optional(),
        "staff-tuition": FamilyId.optional(),
        "parent-tuition": FamilyId.optional(),
      })
      .strict()
      .optional(),
    openBalanceCents: z.number().int().optional(),
    surfaceFinancialAidApplicationIds: z
      .object({
        "staff-financial-aid": z.string().min(1).optional(),
        "parent-financial-aid": z.string().min(1).optional(),
      })
      .strict()
      .optional(),
  })
  .strict();

export const DemoRolePlayerValidationIssueCodeSchema = z.enum([
  "missing_target",
  "duplicate_resolution",
  "cross_tenant",
  "drifted_mapping",
  "orphaned_student",
  "cross_surface_identity_drift",
  "baseline_drift",
]);

export const DemoRolePlayerValidationIssueSchema = z
  .object({
    code: DemoRolePlayerValidationIssueCodeSchema,
    rolePlayerKey: z.string().min(1),
    environment: DemoRolePlayerEnvironmentSchema,
    detail: z.string().min(1),
  })
  .strict();

export const DemoRolePlayerValidationResultSchema = z
  .object({
    valid: z.boolean(),
    issues: z.array(DemoRolePlayerValidationIssueSchema),
  })
  .strict();

export const DemoRolePlayerBaselineRestorePreviewSchema = z
  .object({
    rolePlayerKey: z.string().min(1),
    environment: DemoRolePlayerEnvironmentSchema,
    scenarioId: z.string().min(1),
    recipeId: QaTuitionBalanceResetRecipeIdSchema,
    displayLabel: z.string().min(1),
    currentOpenBalanceCents: z.number().int().nullable(),
    preparedRecipeInput: QaTuitionBalanceResetPreviewInputSchema,
    assertions: z.array(DemoRolePlayerBaselineAssertionSchema).min(1),
    validationIssues: z.array(DemoRolePlayerValidationIssueSchema),
    blockers: z.array(DemoRolePlayerValidationIssueSchema),
    canApply: z.boolean(),
    writePerformed: z.literal(false),
  })
  .strict();

export const DemoRolePlayerBaselineRestoreLivePreviewSchema = z
  .object({
    registryPreview: DemoRolePlayerBaselineRestorePreviewSchema,
    tuitionPreview: QaTuitionBalanceResetPreviewSchema,
  })
  .strict();

export const DemoRolePlayerBaselineRestoreApplyResultSchema =
  QaTuitionBalanceResetApplyResultSchema.extend({
    rolePlayerKey: z.string().min(1),
    scenarioId: z.string().min(1),
    resolution: DemoRolePlayerResolutionSchema,
    validation: DemoRolePlayerValidationResultSchema,
  });

export type DemoRolePlayerRegistry = z.infer<
  typeof DemoRolePlayerRegistrySchema
>;
export type DemoRolePlayerEnvironment = z.infer<
  typeof DemoRolePlayerEnvironmentSchema
>;
export type DemoRolePlayerResolution = z.infer<
  typeof DemoRolePlayerResolutionSchema
>;
export type DemoRolePlayerValidationIssue = z.infer<
  typeof DemoRolePlayerValidationIssueSchema
>;
export type DemoRolePlayerValidationResult = z.infer<
  typeof DemoRolePlayerValidationResultSchema
>;
export type DemoRolePlayerBaselineRestorePreview = z.infer<
  typeof DemoRolePlayerBaselineRestorePreviewSchema
>;
export type DemoRolePlayerBaselineRestoreLivePreview = z.infer<
  typeof DemoRolePlayerBaselineRestoreLivePreviewSchema
>;
export type DemoRolePlayerBaselineRestoreApplyResult = z.infer<
  typeof DemoRolePlayerBaselineRestoreApplyResultSchema
>;

const matchesExpectedValue = (
  expected: string | undefined,
  actual: string | undefined,
): boolean => expected === undefined || expected === actual;

export const validateDemoRolePlayerResolutions = ({
  registry,
  resolutions,
}: {
  registry: DemoRolePlayerRegistry;
  resolutions: readonly DemoRolePlayerResolution[];
}): DemoRolePlayerValidationResult => {
  const issues: DemoRolePlayerValidationIssue[] = [];

  registry.rolePlayers.forEach((rolePlayer) => {
    rolePlayer.targets.forEach((target) => {
      const matches = resolutions.filter(
        (resolution) =>
          resolution.rolePlayerKey === rolePlayer.key &&
          resolution.environment === target.environment,
      );

      if (
        matches.length === 0 ||
        matches.every((resolution) => !resolution.found)
      ) {
        issues.push({
          code: "missing_target",
          rolePlayerKey: rolePlayer.key,
          environment: target.environment,
          detail: "The configured role-player target could not be resolved.",
        });
        return;
      }

      if (matches.length > 1) {
        issues.push({
          code: "duplicate_resolution",
          rolePlayerKey: rolePlayer.key,
          environment: target.environment,
          detail: "The role-player target resolved more than once.",
        });
        return;
      }

      const resolution = matches[0];
      if (
        resolution.orgId !== target.orgId ||
        resolution.schoolId !== target.schoolId ||
        resolution.tenantSlug !== target.tenantSlug
      ) {
        issues.push({
          code: "cross_tenant",
          rolePlayerKey: rolePlayer.key,
          environment: target.environment,
          detail:
            "The resolved organization, school, or tenant does not match the approved target.",
        });
      }

      if (
        !matchesExpectedValue(
          target.userEmail?.toLowerCase(),
          resolution.userEmail?.toLowerCase(),
        ) ||
        !matchesExpectedValue(target.familyId, resolution.familyId) ||
        (resolution.financialAidApplicantId !== undefined &&
          !matchesExpectedValue(
            target.financialAidApplicantId,
            resolution.financialAidApplicantId,
          )) ||
        !matchesExpectedValue(target.staffId, resolution.staffId)
      ) {
        issues.push({
          code: "drifted_mapping",
          rolePlayerKey: rolePlayer.key,
          environment: target.environment,
          detail:
            "The resolved user, family, or staff locator drifted from the registry.",
        });
      }

      if (
        target.familyId &&
        resolution.studentFamilyIds?.some(
          (familyId) => familyId !== target.familyId,
        )
      ) {
        issues.push({
          code: "orphaned_student",
          rolePlayerKey: rolePlayer.key,
          environment: target.environment,
          detail:
            "At least one resolved student is linked to a different family.",
        });
      }

      const scenarios = rolePlayer.baselineScenarios.filter((scenario) =>
        scenario.environments.includes(target.environment),
      );
      scenarios.forEach((scenario) => {
        scenario.assertions.forEach((assertion) => {
          if (
            assertion.kind === "family_open_balance" &&
            resolution.openBalanceCents !== assertion.expectedCents
          ) {
            issues.push({
              code: "baseline_drift",
              rolePlayerKey: rolePlayer.key,
              environment: target.environment,
              detail: `Open balance does not match baseline scenario ${scenario.id}.`,
            });
          }

          if (assertion.kind === "shared_family_identity") {
            const surfaceFamilyIds = assertion.surfaces.map(
              (surface) => resolution.surfaceFamilyIds?.[surface],
            );
            if (surfaceFamilyIds.some((familyId) => !familyId)) {
              issues.push({
                code: "missing_target",
                rolePlayerKey: rolePlayer.key,
                environment: target.environment,
                detail: `Baseline scenario ${scenario.id} is missing a required surface family identity.`,
              });
            } else if (
              surfaceFamilyIds.some((familyId) => familyId !== target.familyId)
            ) {
              issues.push({
                code: "cross_surface_identity_drift",
                rolePlayerKey: rolePlayer.key,
                environment: target.environment,
                detail: `Baseline scenario ${scenario.id} resolves different family identities across surfaces.`,
              });
            }
          }
        });
      });
    });
  });

  return DemoRolePlayerValidationResultSchema.parse({
    valid: issues.length === 0,
    issues,
  });
};

export const previewDemoRolePlayerBaselineRestore = ({
  registry,
  resolutions,
  rolePlayerKey,
  environment,
  scenarioId,
}: {
  registry: DemoRolePlayerRegistry;
  resolutions: readonly DemoRolePlayerResolution[];
  rolePlayerKey: string;
  environment: z.infer<typeof DemoRolePlayerEnvironmentSchema>;
  scenarioId: string;
}): DemoRolePlayerBaselineRestorePreview => {
  const rolePlayer = registry.rolePlayers.find(
    (candidate) => candidate.key === rolePlayerKey,
  );
  if (!rolePlayer) {
    throw new Error(`Unknown Demo role-player ${rolePlayerKey}.`);
  }

  const target = rolePlayer.targets.find(
    (candidate) => candidate.environment === environment,
  );
  if (!target?.familyId) {
    throw new Error(
      `Demo role-player ${rolePlayerKey} has no tuition family target in ${environment}.`,
    );
  }

  const scenario = rolePlayer.baselineScenarios.find(
    (candidate) =>
      candidate.id === scenarioId &&
      candidate.environments.includes(environment),
  );
  if (!scenario) {
    throw new Error(
      `Demo role-player ${rolePlayerKey} has no baseline scenario ${scenarioId} in ${environment}.`,
    );
  }
  if (scenario.recipeId !== "restore_tuition_open_balance") {
    throw new Error(
      `Demo role-player baseline scenario ${scenarioId} is not a tuition recipe.`,
    );
  }

  const selectedResolution = resolutions.find(
    (resolution) =>
      resolution.rolePlayerKey === rolePlayerKey &&
      resolution.environment === environment,
  );
  const validationIssues = validateDemoRolePlayerResolutions({
    registry,
    resolutions,
  }).issues.filter(
    (issue) =>
      issue.rolePlayerKey === rolePlayerKey &&
      issue.environment === environment,
  );
  const blockers = validationIssues.filter(
    (issue) => issue.code !== "baseline_drift",
  );

  return DemoRolePlayerBaselineRestorePreviewSchema.parse({
    rolePlayerKey,
    environment,
    scenarioId,
    recipeId: scenario.recipeId,
    displayLabel: rolePlayer.displayLabel,
    currentOpenBalanceCents: selectedResolution?.openBalanceCents ?? null,
    preparedRecipeInput: {
      orgId: target.orgId,
      familyId: target.familyId,
      academicYear: scenario.academicYear,
      targetOpenBalanceCents: scenario.targetOpenBalanceCents,
    },
    assertions: scenario.assertions,
    validationIssues,
    blockers,
    canApply: blockers.length === 0,
    writePerformed: false,
  });
};
